// ─────────────────────────────────────────────────────────────────────────────
// 🚨 `next` ON A LOGIN PAGE IS AN OPEN-REDIRECT PRIMITIVE.
//
// It arrives in a URL, so it is attacker-controllable, and an absolute value
// turns a real Doorly login into a phishing hop: the victim checks the domain,
// sees godoorly.com, signs in, and is handed to somebody else's host — possibly
// a pixel-perfect copy asking them to sign in "again".
//
// 🔑 WHY IT EXISTS AT ALL: the partner activation emails all point at
// /doorlypro/onboarding, which redirects an unauthenticated visitor to the login
// page. Without carrying the destination, the fix trades one dead end for
// another — the partner clicks "open the eligibility tool", logs in, and lands on
// a dashboard, having lost the thing they clicked.
//
// ⚠️ AN ALLOW-RULE, NOT A DENY-LIST. Three things must all hold: one leading
// slash, not two, and inside the /doorlypro/ subtree. A list of bad prefixes is a
// list of the ones somebody thought of — 'javascript:', '\\evil.com', '%2f%2f',
// 'https:/evil.com' — and the next encoding is not on it.
//
// ⚠️ '//' IS THE ONE THAT LOOKS SAFE AND IS NOT. '//evil.com/doorlypro/x' starts
// with '/' and contains '/doorlypro/', and browsers treat it as protocol-relative
// — i.e. absolute. It is rejected explicitly, before the subtree check, because
// the subtree check alone would pass it.
//
// 🔑 EXTRACTED FROM THE COMPONENT SO IT CAN BE TESTED AS A FUNCTION. The guard
// was an inline IIFE, which is exactly the shape that gets a comment claiming it
// is safe and no assertion proving it.
// ─────────────────────────────────────────────────────────────────────────────

// The subtree a `next` may point into. Login is DoorlyPro's; sending a partner
// session into /admin or /app on the strength of a query parameter is a different
// product's authority.
export const NEXT_PREFIX = '/doorlypro/';

/**
 * @param {string} search  location.search, e.g. '?next=%2Fdoorlypro%2Fonboarding'
 * @returns {string|null}  a safe same-origin path, or null to use the default
 */
export function safeNextPath(search) {
  let raw;
  try {
    raw = new URLSearchParams(search || '').get('next');
  } catch (_) {
    // ⚠️ A MALFORMED QUERY IS NOT A DESTINATION. URLSearchParams is lenient, but
    // a throw here must land on the default rather than on the caller.
    return null;
  }
  if (!raw || typeof raw !== 'string') return null;
  // ⚠️ ONE leading slash. '//' first, because it would otherwise satisfy the
  // subtree test below.
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  // ⚠️ A BACKSLASH IS A SLASH TO SOME BROWSERS. '/\evil.com' and '/\/evil.com'
  // have both been treated as protocol-relative; there is no legitimate
  // backslash in one of our paths.
  if (raw.includes('\\')) return null;
  // ⚠️ THE TRAILING SLASH IN THE PREFIX IS LOAD-BEARING. '/doorlypro' alone would
  // also match '/doorlypro-evil', and '/doorlyproxy' — a sibling route added
  // later would silently become a valid target.
  if (!raw.startsWith(NEXT_PREFIX)) return null;
  return raw;
}
